import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

import { AppConfigService } from 'app/services/app-config/app-config.service';
import { DialogGeneralMessageComponent } from 'app/components/modal/dialog-general-message/dialog-general-message.component';
import { MdDialog } from '@angular/material';

@Component({
  selector: 'upload-file',
  templateUrl: './upload-file.component.html',
  styleUrls: ['./upload-file.component.scss']
})
export class UploadFileComponent implements OnInit {

  @Input() options = { id: 'file' };
  @Input() maximumSize = 2097152; // default size: 2 MB
  @Input() maxFile = 1;
  @Input() isLoading = false;
  @Output() onUpload = new EventEmitter();
  @Output() onDeleteFile = new EventEmitter();
  @ViewChild('inputFile') inputFileElement: ElementRef;

  private readonly STATE = { NO_ATTACH: 'no-attach', ATTACHING: 'attaching', FULL_ATTACH: 'full-attach' };
  private readonly FileTypes = { PNG: 'png', JPG: 'jpg', PDF: 'pdf', MSG: 'msg', EML: 'eml' };
  private defaultErrors = { overflowSize: false, invalidFileType: false, notSelected: false };

  private currentFile: File[] = [];
  private currentState = this.STATE.NO_ATTACH;
  private errors = this.defaultErrors;
  private fileReader = new FileReader();
  private preloadedFileSizes = new Map<number, number>();

  constructor(
    private appConfigService: AppConfigService,
    private dialog: MdDialog,
  ) { }

  ngOnInit() { }

  // tslint:disable:no-unused-variable
  private getCurrentFileName(index) {
    return this.currentFile.length > 0 ? this.currentFile[index].name : 'No attached file';
  }

  private getCurrentFileSize(index) {
    const size = this.preloadedFileSizes.has(index) ? this.preloadedFileSizes.get(index) : (this.currentFile[index] ? this.currentFile[index].size : 0);
    return this.bytesToHumanSize(size);
  }

  private canDeleteFile(index) {
    return !this.preloadedFileSizes.has(index);
  }

  private onFileUploadChanged(event) {
    if (event && event.target && event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      if (this.setValidated([file])) {
        const fileIndex = this.currentFile.push(file);
        this.setState();
        this.onUpload.emit({file: file, fileIndex: fileIndex - 1, fileName: file.name, componentId: this.options.id});
      } else {
        this.inputFileElement && (this.inputFileElement.nativeElement.value = '');
      }
    }
  }

  public onCloseButtonClicked(index) {
    this.currentFile[index] = null;
    this.currentFile = this.currentFile.filter(file => file != null);
    this.setState();
    this.inputFileElement && (this.inputFileElement.nativeElement.value = '');
    this.onDeleteFile.emit(index);
  }

  private bytesToHumanSize(bytes) {
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    if (bytes === 0) {
      return '0 Byte';
    }
    const index = (Math.floor(Math.log(bytes) / Math.log(1024)));
    return Math.round(bytes / Math.pow(1024, index)) + ' ' + sizes[index];
  }

  private showErrorWithMessage(message) {
    const title = this.appConfigService.getTranslate('checkout-3-attach-file-title');
    const mainBtn = this.appConfigService.getTranslate('btn-ok');
    const config = {
      data: { icon: 'icon-service-failed', title, message, mainBtn }
    };
    this.dialog.open(DialogGeneralMessageComponent, config);
  }

  setValidated(files: File[] = this.currentFile): boolean {
    let validate = true;
    if (files && files.length === 0) {
      const message = this.appConfigService.getTranslate('checkout-3-attach-file-validation-file-not-attached');
      this.showErrorWithMessage(message);
      this.errors.notSelected = true;
      return false;
    }

    files.forEach((file) => {
      if (file.size > this.maximumSize) {
        const message = this.appConfigService.getTranslate('checkout-3-attach-file-validation-file-size-overflow');
        this.showErrorWithMessage(message);
        this.errors.overflowSize = true;
        validate = false;
        return;
      }

      if (!this.getFileType(file)) {
        const message = this.appConfigService.getTranslate('checkout-3-attach-file-validation-invalid-file-type');
        this.showErrorWithMessage(message);
        this.errors.invalidFileType = true;
        validate = false;
        return;
      }
    });

    return validate;
  }

  isAttached() {
    return this.currentFile && this.currentFile.length > 0;
  }

  getFileType(file) {
    if (!file) {
      return undefined;
    }

    return this.validateFileType(file);
  }

  getFileTypeForDisplay(index) {
    const file = this.currentFile[index];
    if (!file) {
      return undefined;
    }

    return this.validateFileType(file);
  }

  private validateFileType(file) {
    let currentType = undefined;
    if (/\/pdf/.test(file.type)) {
      currentType = this.FileTypes.PDF;
    } else if (/\/jpg|\/jpeg/.test(file.type)) {
      currentType = this.FileTypes.JPG;
    } else if (/\/png/.test(file.type)) {
      currentType = this.FileTypes.PNG;
    } else if (/\/rfc822/.test(file.type)) {
      currentType = this.FileTypes.EML;
    } else if (/\/vnd.ms-outlook/.test(file.type) || /\.msg/.test(file.name)) {
      currentType = this.FileTypes.MSG;
    }
    return currentType;
  }

  async getBase64Images() {
    return new Promise((resolve, reject) => {
      if (!this.currentFile) {
        reject('file empty!');
      }

      this.fileReader.readAsDataURL(this.currentFile[0]);
      this.fileReader.onload = () => {
        resolve(this.fileReader.result);
      };
      this.fileReader.onerror = (err) => {
        console.error('[UploadFileComponent] read file error', err);
        reject(err);
      };
    });
  }

  public getCurrentFile(): File[] {
    return this.currentFile || null;
  }

  public setPreloadedFiles(attachments: Array<{ originalFileName: string, fileType: string, fileSize: number }>) {
    const mimeTypeMap: { [key: string]: string } = {
      pdf: 'application/pdf',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      eml: 'message/rfc822',
      msg: 'application/vnd.ms-outlook',
    };
    attachments.forEach(attachment => {
      const fileType = attachment.fileType ? attachment.fileType.toLowerCase() : '';
      const mimeType = mimeTypeMap[fileType] || fileType;
      const index = this.currentFile.push(new File([''], attachment.originalFileName, { type: mimeType })) - 1;
      this.preloadedFileSizes.set(index, attachment.fileSize);
    });
    this.setState();
  }

  private setState() {
    if (this.currentFile.length === 0) {
      this.currentState = this.STATE.NO_ATTACH;
    } else if (this.currentFile.length === this.maxFile) {
      this.currentState = this.STATE.FULL_ATTACH;
    } else {
      this.currentState = this.STATE.ATTACHING;
    }
  }

}



// WEBPACK FOOTER //
// ./src/app/components/upload-file/upload-file.component.ts